import React from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { colors } from '@/design-system/theme/colors';
import { CreditStatus } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';

interface ZeroCreditBlockerProps {
  visible: boolean;
  creditStatus: CreditStatus | null;
  onClose: () => void;
  onBuyCredits: () => void;
  onUpgradePlan?: () => void;
}

/**
 * ZeroCreditBlocker - Modal shown when user tries to analyze with 0 credits
 *
 * Features:
 * - Clear messaging about exhausted credits
 * - Primary CTA: Buy Credits
 * - Secondary CTA: Upgrade Plan (if applicable)
 * - Explains what happened and how to continue
 */
export function ZeroCreditBlocker({
  visible,
  creditStatus,
  onClose,
  onBuyCredits,
  onUpgradePlan,
}: ZeroCreditBlockerProps) {
  const { t } = useTranslation();

  if (!visible) return null;

  const subscriptionTotal = creditStatus?.subscription_credits?.total ?? 0;
  const hasSubscription = subscriptionTotal > 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View className="flex-1 items-center justify-center bg-black/50 px-4">
        <View className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with Icon */}
          <View className="items-center pt-6 pb-4 px-6 bg-red-50">
            <View className="w-16 h-16 rounded-full bg-red-100 items-center justify-center mb-3">
              <MaterialIcons name="error-outline" size={36} color={colors.error} />
            </View>
            <Text className="text-xl font-bold text-gray-900 text-center">
              {t('dashboard.noCreditsAvailable')}
            </Text>
          </View>

          {/* Content */}
          <View className="px-6 py-5">
            <Text className="text-sm text-gray-600 text-center leading-5">
              {hasSubscription
                ? t('dashboard.usedAllCredits')
                    .replace('{{total}}', String(subscriptionTotal))
                : t('dashboard.noCreditsToAnalyze')}
            </Text>

            {hasSubscription && (
              <View className="mt-4 flex-row items-center justify-center gap-2 py-2 px-3 bg-gray-50 rounded-lg">
                <MaterialIcons name="info-outline" size={16} color={colors.gray[500]} />
                <Text className="text-xs text-gray-500">
                  {t('dashboard.creditsWillRefresh')}
                </Text>
              </View>
            )}

            <Text className="text-sm text-gray-700 text-center mt-4 font-medium">
              {t('dashboard.getMoreCredits')}
            </Text>
          </View>

          {/* Actions */}
          <View className="px-6 pb-6 gap-3">
            {/* Primary: Buy Credits */}
            <TouchableOpacity
              onPress={onBuyCredits}
              className="flex-row items-center justify-center gap-2 py-3.5 rounded-xl bg-primary"
              activeOpacity={0.8}
            >
              <MaterialIcons name="add-circle" size={20} color="white" />
              <Text className="text-base font-semibold text-white">
                {t('dashboard.buyMoreCreditsButton')}
              </Text>
            </TouchableOpacity>

            {/* Secondary: Upgrade Plan */}
            {onUpgradePlan && (
              <TouchableOpacity
                onPress={onUpgradePlan}
                className="flex-row items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 bg-white"
                activeOpacity={0.7}
              >
                <MaterialIcons name="upgrade" size={20} color={colors.primary} />
                <Text className="text-base font-semibold text-primary">
                  {t('dashboard.upgradePlan')}
                </Text>
              </TouchableOpacity>
            )}

            {/* Dismiss */}
            <TouchableOpacity
              onPress={onClose}
              className="py-2"
              activeOpacity={0.7}
            >
              <Text className="text-sm text-gray-500 text-center">
                {t('dashboard.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

export default ZeroCreditBlocker;
